import Seo from "@/components/layout/Seo";
import Reveal from "@/components/layout/Reveal";
import SectionCTA from "@/components/marketing/SectionCTA";
import MapEmbed from "@/components/marketing/MapEmbed";
import { ZONES, SITE } from "@/lib/site";
import { MapPin, Phone, Mail } from "lucide-react";

export default function ZonePage() {
    return (
        <>
            <Seo
                title="Zone d’intervention"
                description="RCPS Terrassement intervient à Genève et en Suisse romande : Carouge, Lancy, Vernier, Meyrin, Onex, Plan-les-Ouates, Grand-Saconnex, Nyon, Lausanne."
                noindex={true}
            />
            <section className="relative bg-navy-700 text-white overflow-hidden">
                <img src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=2400&q=80" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
                <div className="absolute inset-0 bg-gradient-to-b from-navy-800/80 via-navy-700/70 to-navy-700" />
                <div className="absolute inset-0 bg-topo-dark opacity-40 mix-blend-overlay" />
                <div className="absolute inset-0 noise-overlay" />
                <div className="relative rcps-container pt-24 md:pt-28 pb-16 md:pb-24 max-w-4xl">
                    <Reveal>
                        <p className="kicker text-white/80"><span className="hairline bg-white/60" /> Zone d’intervention</p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1 className="mt-4 font-heading text-4xl md:text-6xl font-semibold leading-[1.05] tracking-tight">
                            Entreprise de terrassement à Genève et en Suisse romande.
                        </h1>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-6 text-white/80 max-w-2xl text-base md:text-lg leading-relaxed">
                            RCPS Terrassement intervient principalement sur l’ensemble du canton de Genève et, selon les projets, dans les cantons voisins.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="bg-white">
                <div className="rcps-container py-20 md:py-24 grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <Reveal>
                            <div className="rounded-md overflow-hidden border border-concrete-200 aspect-[4/3] relative">
                                <MapEmbed
                                    address="Genève, Suisse"
                                    label="Carte Genève"
                                    testId="zone-map"
                                />
                            </div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div className="mt-6 rounded-md border border-concrete-200 bg-white p-6">
                                <p className="kicker text-navy-700"><span className="hairline" /> Adresse</p>
                                <p className="mt-3 text-navy-700 font-heading text-lg">
                                    {SITE.address.street}
                                    <br />
                                    {SITE.address.zip} {SITE.address.city}, {SITE.address.country}
                                </p>
                                <div className="mt-4 flex flex-wrap gap-4">
                                    <a href={`tel:${SITE.phoneTel}`} className="inline-flex items-center gap-2 text-sm font-semibold text-navy-700">
                                        <Phone className="w-4 h-4" /> {SITE.phone}
                                    </a>
                                    <a href={`mailto:${SITE.email}`} className="inline-flex items-center gap-2 text-sm font-semibold text-navy-700">
                                        <Mail className="w-4 h-4" /> {SITE.email}
                                    </a>
                                </div>
                            </div>
                        </Reveal>
                    </div>
                    <div className="md:col-span-7">
                        <Reveal delay={0.1}>
                            <p className="kicker text-navy-700"><span className="hairline" /> Zones couvertes</p>
                            <h2 className="mt-3 font-heading text-3xl md:text-4xl font-semibold text-navy-700 tracking-tight">
                                Genève, Vaud, Suisse romande.
                            </h2>
                        </Reveal>
                        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {ZONES.map((z, i) => (
                                <Reveal key={z.name} delay={(i % 4) * 0.03}>
                                    <div className="border border-concrete-200 rounded-md p-5 bg-white flex items-start gap-4">
                                        <MapPin className="w-4 h-4 text-steel mt-1" />
                                        <div>
                                            <p className="font-heading text-navy-700 font-semibold">{z.name}</p>
                                            <p className="mt-1 text-sm text-navy-700/70 leading-relaxed">{z.desc}</p>
                                        </div>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <SectionCTA
                kicker="Vous avez un projet ?"
                title="Un chantier à Genève ou en Suisse romande ?"
                text="Contactez-nous pour une estimation. Nous étudions votre projet et vous répondons rapidement."
            />
        </>
    );
}
